/**
 * Fancy Dress Central — site header (layout metrics + GO Outdoors auto-hide).
 * CSS: assets/fdc-header.css | Markup: sections/header.liquid
 */
(function () {
  const BUILD = 'auto-hide-v3';
  const SCROLL_THRESHOLD = 48;
  const TOP_SNAP = 8;
  const SCROLL_DELTA = 6;

  function getHeader() {
    return document.querySelector('[data-site-header]');
  }

  function getShell(header) {
    return header?.closest('.shopify-section.cc-header') || header?.closest('.shopify-section');
  }

  function getScrollTop() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDesktop() {
    return window.matchMedia('(min-width: 769px)').matches;
  }

  function getAnnouncementHeight() {
    const el =
      document.getElementById('shopify-section-announcement') ||
      document.querySelector('.shopify-section[id*="announcement"]');
    if (!el || el.offsetParent === null) return 0;
    return el.offsetHeight;
  }

  /**
   * Single source for --header-height, --fdc-header-spacer, --header-end.
   */
  function syncHeaderMetrics(header) {
    if (!header) return;
    const shell = getShell(header);
    const hidden = shell?.dataset.scrollState === 'away';
    const desktop = isDesktop();

    // Batch layout reads before any style writes to avoid forced reflow.
    const shellHeight = hidden ? 0 : shell?.offsetHeight || header.offsetHeight;
    const announcementHeight = hidden ? 0 : getAnnouncementHeight();
    const totalHeight = shellHeight + announcementHeight;
    let headerEnd = 0;
    if (!hidden) {
      const rect = (shell || header).getBoundingClientRect();
      const chromeHeight = shell?.offsetHeight || header.offsetHeight;
      headerEnd = rect.top + chromeHeight;
    }

    document.documentElement.style.setProperty('--header-height', `${totalHeight}px`);
    document.documentElement.style.setProperty('--fdc-header-spacer', `${shellHeight}px`);
    document.documentElement.style.setProperty('--header-end', `${headerEnd.toFixed(1)}px`);
    document.documentElement.style.setProperty(
      '--header-end-padded',
      `${(headerEnd + (desktop ? 56 : 20)).toFixed(1)}px`
    );

    if (shell) {
      shell.classList.toggle('fdc-header-shell--hidden', hidden);
      shell.classList.toggle(
        'fdc-header-shell--will-animate',
        Boolean(header.hasAttribute('data-sticky') && !hidden && !prefersReducedMotion())
      );
    }
  }

  window.fdcSyncHeaderMetrics = syncHeaderMetrics;

  let measureFrame = 0;

  function measureChrome(header) {
    if (!header) return;

    const nav = header.querySelector('.site-header__nav');
    const search = header.querySelector('.site-header__search-inner');
    const navHeight = nav ? nav.scrollHeight : null;
    const searchHeight = search ? search.scrollHeight : null;

    if (navHeight !== null) {
      header.style.setProperty('--site-header-nav-height', `${navHeight}px`);
    }
    if (searchHeight !== null) {
      header.style.setProperty('--site-header-search-height', `${searchHeight}px`);
    }

    syncHeaderMetrics(header);
  }

  function scheduleMeasureChrome(header) {
    if (!header) return;
    if (measureFrame) cancelAnimationFrame(measureFrame);
    measureFrame = requestAnimationFrame(() => {
      measureFrame = 0;
      measureChrome(header);
    });
  }

  function initSearchMinimised(header, transitionMs) {
    const searchToggle = header.querySelector('.js-show-search');
    const searchToggleLeft = header.querySelector('.js-show-search-left');
    const searchBar = header.querySelector('.js-search-bar');

    const toggle = (evt) => {
      evt.preventDefault();
      if (!searchBar) return;

      if (header.classList.contains('search-is-collapsed')) {
        header.classList.remove('search-is-collapsed');
        window.setTimeout(() => {
          header.classList.add('search-is-visible');
          searchBar.querySelector('.js-search-input')?.focus();
          scheduleMeasureChrome(header);
        }, transitionMs);
      } else {
        header.classList.remove('search-is-visible');
        window.setTimeout(() => header.classList.add('search-is-collapsed'), 0);
        window.setTimeout(() => scheduleMeasureChrome(header), transitionMs);
      }
    };

    searchToggle?.addEventListener('click', toggle);
    searchToggleLeft?.addEventListener('click', toggle);
  }

  function initCartShake(header) {
    const icon = header.querySelector('.header__icon--cart-shake');
    if (!icon || !window.theme?.storageUtil) return;

    let pageCount = window.theme.storageUtil.get('shake-page-count', false, true);
    pageCount = pageCount ? parseInt(pageCount, 10) + 1 : 1;
    const shakeFrequency = parseInt(icon.dataset.shakeFrequency, 10);

    if (pageCount < shakeFrequency) {
      icon.classList.remove('header__icon--cart-shake');
    } else {
      pageCount = 0;
    }
    window.theme.storageUtil.set('shake-page-count', pageCount, true);
  }

  function initAutoHide(header, shell) {
    if (!header.hasAttribute('data-sticky') || !shell || shell.dataset.fdcScrollInit === 'true') {
      return;
    }

    shell.dataset.fdcScrollInit = 'true';
    header.dataset.fdcScrollReady = 'true';

    let lastY = getScrollTop();
    let ticking = false;

    const setState = (state) => {
      if (shell.dataset.scrollState === state) {
        scheduleMeasureChrome(header);
        return;
      }
      shell.dataset.scrollState = state;
      header.dataset.scrollState = state;
      header.classList.toggle('is-chrome-hidden', state === 'away');
      header.classList.remove('is-out', 'is-fixed-chrome');
      scheduleMeasureChrome(header);
    };

    const updateScroll = () => {
      ticking = false;

      if (document.body.classList.contains('fixed')) return;

      if (prefersReducedMotion()) {
        setState(getScrollTop() <= TOP_SNAP ? 'top' : 'revealed');
        lastY = getScrollTop();
        return;
      }

      const search = header.querySelector('[data-header-search-panel]');
      if (search?.contains(document.activeElement)) {
        setState(getScrollTop() <= TOP_SNAP ? 'top' : 'revealed');
        lastY = getScrollTop();
        return;
      }

      const y = getScrollTop();
      const dy = y - lastY;

      if (y <= TOP_SNAP) {
        setState('top');
      } else if (y <= SCROLL_THRESHOLD) {
        if (dy < -SCROLL_DELTA) {
          setState('revealed');
        }
      } else if (dy > SCROLL_DELTA) {
        setState('away');
      } else if (dy < -SCROLL_DELTA) {
        setState('revealed');
      }

      lastY = y;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateScroll);
      }
    };

    setState('top');
    window.addEventListener('scroll', onScroll, { passive: true });
    updateScroll();

    window.addEventListener('on:breakpoint-change', () => {
      setState(getScrollTop() <= TOP_SNAP ? 'top' : 'revealed');
      lastY = getScrollTop();
      scheduleMeasureChrome(header);
    });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => scheduleMeasureChrome(header));
      observer.observe(shell);
      const inner = header.querySelector('.site-header__inner');
      if (inner && inner !== shell) {
        observer.observe(inner);
      }
    }
  }

  function bindHeader(header) {
    if (!header || header.dataset.fdcBound === 'true') return;
    header.dataset.fdcBound = 'true';

    const shell = getShell(header);
    const transitionMs =
      parseFloat(getComputedStyle(header).getPropertyValue('--header-transition-speed')) || 300;

    measureChrome(header);
    initAutoHide(header, shell);

    header.querySelector('.main-menu__toggle')?.addEventListener('click', () => {
      scheduleMeasureChrome(header);
    });

    window.addEventListener('on:debounced-resize', () => scheduleMeasureChrome(header));

    if (header.hasAttribute('data-search-minimised')) {
      initSearchMinimised(header, transitionMs);
    }

    initCartShake(header);
  }

  function init() {
    document.documentElement.dataset.fdcHeaderBuild = BUILD;
    bindHeader(getHeader());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (evt) => {
    const header = evt.target?.querySelector?.('[data-site-header]');
    if (header) {
      bindHeader(header);
    }
  });
})();
